import React, { useEffect, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { IconChevron } from '@sparkol/videoscribe-react-ui';

import { useAppDispatch, useAppSelector } from 'js/store/hooks';
import { useProject } from 'js/shared/hooks/useProject';
import { updateScribe } from 'js/actionCreators/scribeActions';
import { ExistingScribeModel } from 'js/types';
import {
  activateVisualStyleSearch,
  navigateToCharacters,
  resetSearch,
  VisualStyleItem
} from 'js/actionCreators/imageSearchActions';
import { ImageLibraryStep } from 'js/reducers/imageSearchReducer';
import { ReactComponent as IconTrash } from 'imgs/svg-icons/IconTrash.svg';
import {
  trackProjectStyleChanged,
  trackProjectStyleRemoved,
  trackProjectStyleSelected
} from 'js/actionCreators/trackingActions';

import StyleSelectorPanel from './StyleSelectorPanel';
import STYLE_ICONS from './styleIcons';
import './StyleSelector.css';

const StyleSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const project = useProject();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  const displayedStep = useAppSelector(state => state.imageSearch.displayedStep);
  const visualStyles = useAppSelector(state => state.imageSearch.visualStyles);
  const visualStylesStatus = useAppSelector(state => state.imageSearch.visualStylesStatus);
  const activeSearchMode = useAppSelector(state => state.imageSearch.activeSearchMode);

  const initialProject = useRef(project);
  const initialDisplayedStep = useRef(displayedStep);
  const initialVisualStyles = useRef(visualStyles);

  // Activate the visual style search on initial mount
  // if a visual style is set
  useEffect(() => {
    ReactTooltip.rebuild();
    const project = initialProject.current;
    const displayedStep = initialDisplayedStep.current;
    const visualStyles = initialVisualStyles.current;

    if (!project) return;
    const { visualStyle } = project.settings;
    if (!visualStyle) return;
    if (!visualStyle || displayedStep !== ImageLibraryStep.CATEGORIES) return;
    const style = visualStyles.find(s => s.facetValue === visualStyle);
    if (style) {
      dispatch(activateVisualStyleSearch({ facetId: style.facetId, facetValue: style.facetValue }));
    }
  }, [dispatch]);

  if (!project) return null;

  const currentStyle = project.settings.visualStyle;
  const currentStyleItem = visualStyles.find(s => s.facetValue === currentStyle);
  const currentStyleIconKey = currentStyleItem?.iconKey ?? 'all';

  // Handle style selection
  const handleSelectStyle = (style: VisualStyleItem) => {
    if (currentStyle) {
      dispatch(trackProjectStyleChanged(currentStyle, style.facetValue));
    } else {
      dispatch(trackProjectStyleSelected(style.facetValue));
    }
    const updatedScribe: ExistingScribeModel = {
      ...project,
      settings: { ...project.settings, visualStyle: style.facetValue }
    };
    dispatch(updateScribe(updatedScribe, true));
    dispatch(activateVisualStyleSearch({ facetId: style.facetId, facetValue: style.facetValue }));
    setIsPanelOpen(false);
  };

  // Handle clearing the selected style
  const handleClearStyle = () => {
    dispatch(trackProjectStyleRemoved());
    const { visualStyle: _, ...settings } = project.settings;
    const updatedScribe: ExistingScribeModel = {
      ...project,
      settings
    };
    dispatch(updateScribe(updatedScribe, true));
    dispatch(resetSearch());
    if (displayedStep === ImageLibraryStep.CHARACTERS || activeSearchMode === 'character') {
      dispatch(navigateToCharacters());
    }
  };

  const togglePanelOpen = () => {
    setIsPanelOpen(prev => !prev);
  };

  return (
    <div className="ImageLibrary__styleSelector__wrapper">
      <div className="ImageLibrary__styleSelector">
        <button
          ref={toggleButtonRef}
          className="ImageLibrary__styleSelector__buttonSelect"
          data-for="style-selection-tooltip"
          data-tip={currentStyle ? 'Change style' : 'Choose a style'}
          onClick={togglePanelOpen}
        >
          <img
            src={STYLE_ICONS[currentStyleIconKey]}
            alt="Thumbnail representing the current project style"
            className="ImageLibrary__styleSelector__icon"
          />
          <span className="ImageLibrary__styleSelector__label">Style: </span>
          <span className="ImageLibrary__styleSelector__value">
            {visualStyles.find(style => style.facetValue === currentStyle)?.label ?? 'All styles'}
          </span>
          <IconChevron className={`ImageLibrary__styleSelector__chevron ${isPanelOpen ? 'rotated' : ''}`} />
        </button>
        {currentStyle && (
          <button
            className="ImageLibrary__styleSelector__buttonClear"
            data-for="style-selection-tooltip"
            data-tip="Clear style selection"
            onClick={handleClearStyle}
            disabled={!currentStyle}
            aria-label="Clear style selection"
          >
            <IconTrash />
          </button>
        )}
        <ReactTooltip id="style-selection-tooltip" className="t-utility-label" delayShow={500} />
      </div>
      {isPanelOpen &&
        (visualStylesStatus === 'error' ? (
          <div className="ImageLibrary__styleSelector__error">Unable to load styles. Please try again later.</div>
        ) : (
          <StyleSelectorPanel
            styles={visualStyles}
            selectedStyle={currentStyle}
            onClose={() => setIsPanelOpen(false)}
            onSelectStyle={handleSelectStyle}
            buttonRef={toggleButtonRef}
          />
        ))}
    </div>
  );
};

export default StyleSelector;
